import { useState } from 'react';
import { FaHashtag, FaLock } from 'react-icons/fa';

import { DBChannel } from '@core/types/types.db';
import { Card, CardContent } from '@frontend/components/ui/card';
import { SettingsModalAddChannels } from '@frontend/Settings/components/SettingsModalAddChannels';
import { SettingsModalChannelPrompt } from '@frontend/Settings/components/SettingsModalChannelPrompt';
import { SettingsImportModal } from '@frontend/Settings/components/SettingsModalImport';
import { useDirectoryRefresher } from '@frontend/shared/hooks/shared.hook.useDirectoryRefresher';
import {
  useDirectoryChannels,
  useDirectoryTeam,
} from '@frontend/shared/hooks/useDirectory';

interface SettingsChannelsProps {
  isReadOnly?: boolean;
}

export function SettingsChannels({
  isReadOnly = false,
}: SettingsChannelsProps) {
  const team = useDirectoryTeam();
  const channels = useDirectoryChannels({
    excludeDeleted: true,
    requireQbInChannel: true,
  });
  const [channelUpdates, setChannelUpdates] = useState<
    Record<string, DBChannel>
  >({});

  const needUpdate = !!team.capture_is_running;
  useDirectoryRefresher(
    {
      team: true,
      channels: true,
    },
    needUpdate,
  );

  return (
    <Card className="mt-10 max-w-[600px]">
      <CardContent>
        <h2 className="text-lg font-semibold">Slack Channels</h2>
        {channels.map((channel) => {
          const isQueued = team.capture_queue?.find((q) =>
            q.startsWith(channel.id),
          );

          const isRunning = team.capture_queue?.[0]?.startsWith(channel.id);
          let importString = '';
          if (isRunning) {
            importString = 'Importing…';
          } else if (isQueued) {
            importString = 'Queued to import';
          }

          // Use updated channel if available, otherwise use original
          const currentChannel = channelUpdates[channel.id] || channel;

          const handleChannelUpdate = (updatedChannel: DBChannel) => {
            setChannelUpdates((prev) => ({
              ...prev,
              [updatedChannel.id]: updatedChannel,
            }));
          };

          return (
            <div key={channel.id} className="mt-2.5 flex items-start">
              {channel.is_private ? (
                <FaLock className="mr-2 mt-0.5 size-4" />
              ) : (
                <FaHashtag className="mr-2 mt-0.5 size-4" />
              )}
              <div className="flex-1">
                <p className="leading-none">{channel.name}</p>
                <p className="mt-0.5 text-sm leading-tight text-gray-500">
                  {importString}
                </p>
              </div>
              <div className="ml-2">
                <SettingsModalChannelPrompt
                  channel={currentChannel}
                  onChannelUpdate={handleChannelUpdate}
                  isReadOnly={isReadOnly}
                />
              </div>
            </div>
          );
        })}
        <div className="mt-4 flex items-center gap-2">
          <SettingsImportModal isReadOnly={isReadOnly} />
          <SettingsModalAddChannels isReadOnly={isReadOnly} />
        </div>
      </CardContent>
    </Card>
  );
}
